import type { z } from "zod/v4";
import type {
  VizDataset,
  VizViewType,
} from "@/lib/ai/visualization/data/schema";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import { barComparisonDefinition } from "@/lib/ai/visualization/views/bar-comparison/definition";
import { barRankingDefinition } from "@/lib/ai/visualization/views/bar-ranking/definition";
import { scatterPlotDefinition } from "@/lib/ai/visualization/views/scatter-plot/definition";
import { tableDefinition } from "@/lib/ai/visualization/views/table/definition";
import { timeSeriesAreaDefinition } from "@/lib/ai/visualization/views/time-series-area/definition";
import { timeSeriesComposedDefinition } from "@/lib/ai/visualization/views/time-series-composed/definition";
import { timeSeriesGroupedLineDefinition } from "@/lib/ai/visualization/views/time-series-grouped-line/definition";
import { timeSeriesLineDefinition } from "@/lib/ai/visualization/views/time-series-line/definition";
import type { VizSpec } from "./compiled-spec";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "./shared/element-builder";
import type {
  ValidateVizSpecArgs,
  VizAuthoringCardDraft,
} from "./shared/view-definition";

export type { VizAuthoringCardDraft } from "./shared/view-definition";
export type { VizViewType };

/**
 * 各 view フォルダの `definition.ts` を集約した SSOT。
 */
export const VIZ_VIEW_DEFINITIONS = {
  "time-series-line": timeSeriesLineDefinition,
  "time-series-grouped-line": timeSeriesGroupedLineDefinition,
  "time-series-area": timeSeriesAreaDefinition,
  "time-series-composed": timeSeriesComposedDefinition,
  "scatter-plot": scatterPlotDefinition,
  "bar-ranking": barRankingDefinition,
  "bar-comparison": barComparisonDefinition,
  table: tableDefinition,
} as const;

export const VIZ_VIEW_TYPES = Object.keys(
  VIZ_VIEW_DEFINITIONS,
) as Array<VizViewType>;

type VizComponentType =
  (typeof VIZ_VIEW_DEFINITIONS)[VizViewType]["componentType"];

const VIZ_VIEW_TYPE_SET = new Set<VizViewType>(VIZ_VIEW_TYPES);

/**
 * 文字列が登録済み view type かを判定する。
 *
 * @remarks
 * view 追加時の drift を避けるため、判定元は registry の SSOT だけに固定する。
 */
function _isVizViewType(value: string): value is VizViewType {
  return VIZ_VIEW_TYPE_SET.has(value as VizViewType);
}

type VizViewAdapter<TViewType extends VizViewType> = {
  componentType: VizComponentType;
  buildElement: (
    args: BuildVizElementArgs<Extract<VizSpec, { type: TViewType }>>,
  ) => BuiltVizElement;
  compileSpec: (args: {
    datasetId: string;
    dataset: VizDataset;
    card: VizAuthoringCardDraft | null;
    view: z.infer<(typeof VIZ_VIEW_DEFINITIONS)[TViewType]["authoringSchema"]>;
  }) => Extract<VizSpec, { type: TViewType }>;
  validateSpec: (
    args: ValidateVizSpecArgs<Extract<VizSpec, { type: TViewType }>>,
  ) => VizAuthoringIssue[];
};

type VizViewAdapterRegistry = {
  [K in VizViewType]: VizViewAdapter<K>;
};

function getVizViewAdapter<TViewType extends VizViewType>(
  viewType: TViewType,
): VizViewAdapter<TViewType> {
  return VIZ_VIEW_ADAPTERS[viewType];
}

/**
 * view type ごとの compile/build/validate dispatch を 1 箇所へ集約した SSOT。
 */
export const VIZ_VIEW_ADAPTERS: VizViewAdapterRegistry = {
  ...VIZ_VIEW_DEFINITIONS,
} as unknown as VizViewAdapterRegistry;

/**
 * spec type に対応する view builder を返す。
 */
function resolveVizViewBuilder<TSpec extends VizSpec>(
  spec: TSpec,
): (args: BuildVizElementArgs<TSpec>) => BuiltVizElement {
  return getVizViewAdapter(spec.type).buildElement as (
    args: BuildVizElementArgs<TSpec>,
  ) => BuiltVizElement;
}

function resolveVizViewValidator<TSpec extends VizSpec>(
  spec: TSpec,
): (args: ValidateVizSpecArgs<TSpec>) => VizAuthoringIssue[] {
  return getVizViewAdapter(spec.type).validateSpec as (
    args: ValidateVizSpecArgs<TSpec>,
  ) => VizAuthoringIssue[];
}

/**
 * VizSpec を chart type 対応の builder registry へ振り分ける。
 */
export function buildVizElementFromSpec<TSpec extends VizSpec>(
  args: BuildVizElementArgs<TSpec>,
): BuiltVizElement {
  const built = resolveVizViewBuilder(args.spec)(args);
  return {
    ...built,
    element: {
      ...built.element,
      // Zod 4.4 系では json-render の catalog 検証が optional visible を
      // nonoptional として扱うため、常時表示の既定値を tree 側へ明示する。
      visible: built.element.visible ?? true,
    },
  };
}

/**
 * View type から描画コンポーネント type を返す。
 */
export function resolveVizComponentTypeForViewType<
  TViewType extends VizViewType,
>(viewType: TViewType): VizComponentType {
  return VIZ_VIEW_ADAPTERS[viewType].componentType;
}

/**
 * 描画 component type から view type を逆引きする。
 */
export function resolveVizViewTypeForComponentType(
  componentType: string,
): VizViewType | null {
  for (const viewType of VIZ_VIEW_TYPES) {
    if (VIZ_VIEW_ADAPTERS[viewType].componentType === componentType) {
      return viewType;
    }
  }
  return null;
}

/**
 * structured authoring payload を renderer-ready `VizSpec` へ変換する。
 *
 * @remarks
 * view 定義は authoring schema / prompt / compile / build を持ち、
 * runtime component schema の集約は `component-schemas.ts` に分離する。
 */
export function compileVizSpecForView<TViewType extends VizViewType>(args: {
  viewType: TViewType;
  datasetId: string;
  dataset: VizDataset;
  card: VizAuthoringCardDraft | null;
  view: z.infer<(typeof VIZ_VIEW_DEFINITIONS)[TViewType]["authoringSchema"]>;
}): Extract<VizSpec, { type: TViewType }> {
  return getVizViewAdapter(args.viewType).compileSpec(args);
}

/**
 * compiled `VizSpec` を view-local validator へ dispatch する。
 *
 * @remarks
 * 各 view が自分の surface rule を持ち、registry は type-safe な振り分けだけを担う。
 */
export function validateVizSpecForView<TSpec extends VizSpec>(
  args: ValidateVizSpecArgs<TSpec>,
): VizAuthoringIssue[] {
  return resolveVizViewValidator(args.spec)(args);
}
